import * as React from 'react';
import sortBy from 'lodash/sortBy';

import Spinner from 'components/spinner/Spinner';
import MixedSelectDropdown from 'components/inputs/mixed-select-dropdown/MixedSelectDropdown';

import ContentCards from 'modules/reporting/components/content-cards/ContentCards';

import { __t, applyTranslations } from 'utils/language';

applyTranslations(require('./reporting-posts.i18n.json'));

import './reporting-posts.scss';

import {
  Data,
  MixedSelectDropdownItem
} from 'types';
import type {
  ContentCardItem,
  ContentCardType
} from 'components/cards/content-card/types';
import type {
  ReportingMetaInfoIGStorySnippetType,
  ReportingSummaryInfluencersMap
} from 'modules/reporting/types';

type Props = {
  posts: Array<ContentCardItem>,
  type: ContentCardType,
  isLoading: boolean,
  influencersMapById: ReportingSummaryInfluencersMap,
  igStorySnippetType?: ReportingMetaInfoIGStorySnippetType,
  onNoPostClick?: () => void,
  onSortingChange?: (sortingType: string) => void
};

const SORT_OPTIONS_DEFAULT = ['total_reactions', 'impressions', 'engagement_rate'];
const SORT_OPTIONS_STORIES = ['total_reactions', 'engagement_rate'];

const getSortingOptions = (type: ContentCardType) => (
  type === 'instagram_story' ? SORT_OPTIONS_STORIES : SORT_OPTIONS_DEFAULT
);

type MappingHandler = (post: ContentCardItem) => number;

const getKPIMapping = (
  type: ContentCardType,
  sort: string
): MappingHandler | undefined | null => {
  let getter;

  switch (sort) { // descending sort
    case 'total_reactions':
      return post => -(post.metrics.likes || 0);
    case 'engagement_rate':
      getter = post => {
        if (type === 'tiktok_video') {
          return -post.metrics.engagementRateFollowers;
        }

        return -post.metrics.engagementRate;
      };
      break;
    case 'impressions':
      getter = post => {
        switch (type) {
          case 'tiktok_video':
            return -post.metrics.views;
          case 'instagram_story':
            return -post.metrics.storiesImpressions;
          case 'instagram_post':
            return -post.metrics.impressions;
          default:
            return 0;
        }
      };
      break;
    default:
      break;
  }

  return getter;
};

const getSortedPosts = (
  posts: Array<ContentCardItem>,
  sort: string,
  type: ContentCardType
): Array<ContentCardItem> => {
  const getter = getKPIMapping(type, sort);

  if (!getter) {
    return posts;
  }

  return sortBy(posts, getter);
};

const ReportingPosts = ({
  influencersMapById,
  igStorySnippetType,
  posts = [],
  type,
  isLoading,
  onNoPostClick,
  onSortingChange
}: Props) => {
  const [sort, setSorting] = React.useState('total_reactions');
  const sortedPosts = getSortedPosts(posts, sort, type);

  const getDropdownItems = (): Array<MixedSelectDropdownItem> => (
    getSortingOptions(type)
      .map(value => ({
        identifier: value,
        isDisabled: false,
        isSelected: sort === value,
        label: __t(`reporting_posts.sorting_options.${value}`),
        value
      }))
  );

  const handleSortChange = (
    updatedItems: Array<MixedSelectDropdownItem>,
    data?: Data | null,
    selectedItem?: Partial<MixedSelectDropdownItem> | null
  ): void => {
    const value = selectedItem?.identifier || updatedItems[0].identifier;

    setSorting(value);

    if (onSortingChange) {
      onSortingChange(value);
    }
  };

  const renderSpinner = (): JSX.Element => (
    <div
      className="reporting-posts__spinner"
      style={{ ...(type === 'tiktok_video' ? { height: 300 } : null) }}
    >
      <Spinner
        position="inline"
        size="small"
        color="light"
      />
    </div>
  );

  const renderContentCards = (): JSX.Element => (
    <ContentCards
      channel={type === 'tiktok_video' ? 'tiktok' : 'instagram'}
      posts={sortedPosts}
      influencersMapById={influencersMapById}
      igStorySnippetType={igStorySnippetType}
      onNoPostClick={onNoPostClick}
    />
  );

  const dropdownItems = getDropdownItems();

  return (
    <div>
      <div className="reporting-posts__heading">
        <div className="reporting-posts__title text-overflow-ellipsis">
          {__t(`reporting_posts.heading_${type}`)}
        </div>
        <div className="reporting-posts__actions">
          <div className="reporting-posts__action">
            <div className="reporting-posts__action-label">
              {`${__t('reporting_posts.sorting_label')}:`}
            </div>
            <MixedSelectDropdown
              key={`reporting-posts__dropdown--${sort}`}
              name="reporting-posts__dropdown"
              items={dropdownItems}
              withSelectedInList
              className="reporting-posts__dropdown"
              onItemsToggle={handleSortChange}
            />
          </div>
        </div>
      </div>
      {
        isLoading
          ? renderSpinner()
          : renderContentCards()
      }
    </div>
  );
};

export default ReportingPosts;
